import React from 'react';
import { Typography } from '../../../../interfaces/typography';
import { SPEEDS } from './constants';
import { formatMsToTimeString } from './timeUtils';
import { DownloadIcon, DownloadState, getDownloadTooltip, getDownloadButtonClassName, isDownloadDisabled } from '../../../../components/common/DownloadIcons';

interface ControlsProps {
  isPlaying: boolean;
  currentSpeed: number;
  currentTimeInMS: number;
  endTime?: number;
  onPlayPause: () => void;
  onSpeedChange: (speed: number) => void;
  onDownload?: () => void;
  disabled?: boolean;
  // Download state props
  isDownloading?: boolean;
  downloadError?: string | null;
  downloadSuccess?: boolean;
}

const Button: React.FC<{ play: boolean; onPlayerClick: () => void }> = ({ play, onPlayerClick }) => {
  return (
    <div 
      onClick={onPlayerClick} 
      className="hover:bg-slate-300 cursor-pointer" 
      style={{
        width: '100%', 
        height: '100%', 
        padding: '8px', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        border: "1px solid rgba(91, 101, 124, 0.25)", 
        borderRadius: '50%', 
        boxShadow: '0px 3px 3px rgb(0 0 0 / 10%)'
      }}
    >
      {play ? (
        <svg className="w-5/6 h-5/6" viewBox="0 0 40 60">
          <polygon points="0,0 15,0 15,60 0,60" />
          <polygon points="25,0 40,0 40,60 25,60" />
        </svg>
      ) : (
        <svg className="w-5/6 h-5/6" viewBox="0 0 50 60">
          <polygon points="0,0 50,30 0,60" />
        </svg>
      )}
    </div>
  );
};

// Enhanced circular button with tooltip support
const CircularButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
  className?: string;
}> = ({ onClick, children, disabled = false, tooltip, className = "" }) => {
  return (
    <div className="relative group">
      <div 
        onClick={disabled ? undefined : onClick}
        className={`
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-300 cursor-pointer'}
          ${className}
        `} 
        style={{
          width: '30px', 
          minWidth: '30px', 
          height: '30px', 
          minHeight: '30px', 
          padding: '6px', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          border: "1px solid rgba(91, 101, 124, 0.25)", 
          borderRadius: '50%', 
          boxShadow: '0px 3px 3px rgb(0 0 0 / 10%)'
        }}
        title={tooltip} // Fallback native tooltip
      >
        {children}
      </div>
    </div>
  );
};

export const Controls: React.FC<ControlsProps> = ({
  isPlaying,
  currentSpeed,
  currentTimeInMS,
  endTime = 0,
  onPlayPause,
  onSpeedChange,
  onDownload,
  isDownloading = false,
  downloadError,
  downloadSuccess = false,
}) => {
  const handleSpeedClick = () => {
    const nextIndex = SPEEDS.indexOf(currentSpeed) + 1;
    onSpeedChange(SPEEDS[nextIndex % SPEEDS.length])
  }

  // Determine download state
  const downloadState: DownloadState = isDownloading ? 'downloading' : 
                                      downloadSuccess ? 'success' : 
                                      downloadError ? 'error' : 'idle';

  return (
    <div className="w-fit flex flex-col gap-2 items-center p-1 justify-between h-full">
      <div className="flex flex-row gap-1">
        <div style={{ width: '30px', minWidth: '30px', height: '30px', minHeight: '30px' }}>
          <Button play={isPlaying} onPlayerClick={onPlayPause} />
        </div>

        <CircularButton onClick={handleSpeedClick} tooltip={`Playback speed: ${currentSpeed}x`}>
          <Typography variant="caption">
            {currentSpeed + "x"} 
          </Typography>
        </CircularButton>

        {onDownload && (
          <CircularButton 
            onClick={onDownload}
            disabled={isDownloadDisabled(downloadState)}
            tooltip={getDownloadTooltip(downloadState, downloadError)}
            className={getDownloadButtonClassName(downloadState)}
          >
            <DownloadIcon 
              state={downloadState} 
              error={downloadError}
              className="w-4 h-4" 
            />
          </CircularButton>
        )}
      </div>
      
      <div className="flex flex-row gap-1">
        <Typography variant="caption">{formatMsToTimeString(currentTimeInMS)}</Typography>
        <Typography variant="caption">/</Typography>
        <Typography variant="caption">{formatMsToTimeString(endTime)}</Typography>
      </div>
    </div>
  );
}; 